import type { IntlShape } from 'react-intl'
import { min } from 'lib/array/arrayUtils'
import { getKilometersDistanceFromLatLong } from 'lib/flights/getDistanceFromLatLong'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { getCurrentIntl } from 'i18n/util/currentIntl'

export interface TransferForm {
  toAirport: TransferFormTransfer
  toHotel: TransferFormTransfer
}

export interface SingleTransferForm {
  transfer: TransferFormTransfer
}

export interface TransferFormTransfer {
  type: App.ExperienceTransferType
  time: string
  date: string
  flightNumber: string
  travellers: any
}

export function formatTransferTravellers(
  travellers: App.ExperienceTransferTravellers,
  intl?: IntlShape,
) {
  const currentIntl = intl ?? getCurrentIntl()
  let travellerText = formatOccupantsShort({ adults: travellers.adults, children: travellers.children })
  if (travellers.children) {
    const childSeats = travellers.childSeats ?? 0
    const boosterSeats = travellers.boosterSeats ?? 0
    if (travellers.children > 0 && (childSeats > 0 || boosterSeats > 0)) {
      const seats = [
        childSeats > 0 ?
            currentIntl.formatMessage(
              {
                id: 'bt1S11s3T63NwMrMeOvt',
                defaultMessage: '{count, plural, one {# child seat} other {# child seats}}',
                description:
                'Child seat count in transfer traveller summary in the experiences section',
              },
              { count: childSeats },
            ) :
          undefined,
        boosterSeats > 0 ?
            currentIntl.formatMessage(
              {
                id: '5w1bU1ekbmucK_xdlefT',
                defaultMessage: '{count, plural, one {# booster seat} other {# booster seats}}',
                description:
                'Booster seat count in transfer traveller summary in the experiences section',
              },
              { count: boosterSeats },
            ) :
          undefined,
      ].filter((seat): seat is string => Boolean(seat))
      travellerText = currentIntl.formatMessage(
        {
          id: 'placeholder_id_trnstrvl1',
          defaultMessage: '{travellerText} ({seatSummary})',
          description:
            'Transfer traveller summary showing the traveller counts followed by selected child and booster seats',
        },
        {
          travellerText,
          seatSummary: currentIntl.formatList(seats, { type: 'unit' }),
        },
      )
    }
  }
  return travellerText
}

export function formatTransferBaggage(
  baggage: App.OrderTransferItemBaggage,
  intl?: IntlShape,
) {
  const currentIntl = intl ?? getCurrentIntl()
  const bagsText = currentIntl.formatMessage(
    {
      id: 'wOY4k7IQjESHcbpPMTUT',
      defaultMessage: '{count, plural, one {# bag} other {# bags}}',
      description: 'Bag count in transfer baggage summary in the airport transfer booking section',
    },
    { count: baggage.bags },
  )

  if ((baggage.oversizedBags ?? 0) > 0) {
    const oversizedText = currentIntl.formatMessage(
      {
        id: 'xfUeo_I2FjsqSO4OC6jl',
        defaultMessage: '{count, plural, one {# oversized bag} other {# oversized bags}}',
        description: 'Oversized bag count in transfer baggage summary in the experiences section',
      },
      { count: baggage.oversizedBags },
    )

    return currentIntl.formatMessage(
      {
        id: 'placeholder_id_trnsbagg1',
        defaultMessage: '{bagsText} ({oversizedText})',
        description:
          'Transfer baggage summary showing the bag count followed by oversized bags in parentheses',
      },
      {
        bagsText,
        oversizedText,
      },
    )
  }

  return bagsText
}

export function getValidTransferOptions(
  view: App.ExperienceTransferView,
  transfers: Array<App.ExperienceTransferOption>,
) {
  const { hotel, travellers, baggage } = view
  const childCount = travellers.children ?? 0
  const passengers = travellers.adults + childCount
  const childSeats = childCount > 0 ? travellers.childSeats ?? 0 : 0
  const boosterSeats = childCount > 0 ? travellers.boosterSeats ?? 0 : 0
  const hotelDistance = hotel.kmFromAirport ?? 0
  return transfers.filter(transfer => {
    return transfer.maxPassengers >= passengers &&
      transfer.maxTransferDistance >= hotelDistance &&
      (childSeats === 0 || transfer.hasChildSeats) &&
      (boosterSeats === 0 || transfer.hasBoosterSeats) &&
      (baggage && (baggage.bags === 0 || transfer.maxCheckedBags >= baggage.bags))
  })
}

export type NoTransferReason = 'distance' | 'passengers' | 'childSeats' | 'bags'
export function findBestTransfer(
  view: App.ExperienceTransferView,
  transfers: Array<App.ExperienceTransferOption>,
): {
  bestTransfer?: App.ExperienceTransferOption
  bestLuxPlusTransfer?: App.ExperienceTransferOption
  noTransferReason?: NoTransferReason
} {
  const { hotel, travellers, baggage } = view
  if (hotel.kmFromAirport) {
    const passengers = travellers.adults + (travellers.children ?? 0)
    const validTransfers = getValidTransferOptions(view, transfers)

    if (validTransfers.length === 0) {
      // now work out why it's not valid, proritise distance first as it's the most important
      let reason: NoTransferReason
      if (transfers.some(transfer => hotel.kmFromAirport && transfer.maxTransferDistance >= hotel.kmFromAirport)) {
        // too far away
        reason = 'distance'
      } else if (transfers.some(transfer => transfer.maxPassengers >= passengers)) {
        // must have too many passengers
        reason = 'passengers'
      } else if (baggage && baggage.bags > 0 && transfers.some(transfer => transfer.maxCheckedBags >= baggage.bags)) {
        // must have too many bags
        reason = 'bags'
      } else {
        // only reason left
        reason = 'childSeats'
      }

      return {
        bestTransfer: undefined,
        bestLuxPlusTransfer: undefined,
        noTransferReason: reason,
      }
    }

    return {
      bestTransfer: min(validTransfers, transfer => transfer.price),
      bestLuxPlusTransfer: min(validTransfers.filter(transfer => transfer.memberPrice > 0), transfer => transfer.memberPrice),
    }
  }

  return {}
}

export function getDistanceBetweenPropertyAndAirport(property?: App.OfferProperty | App.BedbankProperty, airport?: App.Airport) {
  if (property && airport) {
    return getKilometersDistanceFromLatLong(
      property.latitude,
      property.longitude,
      airport.latitude,
      airport.longitude,
    )
  }

  return undefined
}
